import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IAlbum } from '@/data/models/album';
import {
  usePricingModal,
  PricingData,
  buildPriceDropdownData,
} from '@/hooks/usePricingModal';
import styles from './PricingModal.module.scss';
import CancelChangesModal from '@/components/common/CancelChangesModal/cancelChangesModal';
import ConfirmationModal from '@/components/common/ConfirmationModal/ConfirmationModal';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { IPrice, ISalesTeam } from '@/data/models/salesTerritory';
import { useNestedModalContext } from '@/context/NestedModalContext';
import { IChangeRequest } from '@/data/models/changeRequest';
import { ICurrentPricingChangesGroup } from '@/types/product-current-changes-types';
import ChangeRequestIndicator from '@/components/common/ChangeRequestIndicator/ChangeRequestIndicator';
import DismissibleErrorAlert from '@/components/common/ErrorAlert/DismissibleErrorAlert';
import { usePricingCurrentChanges } from '@/hooks/usePricingCurrentChanges';

interface PricingModalProps {
  togglePricingModal: () => void;
  album: IAlbum;
  readonly: boolean;
  updateProductPricing: (
    albumId: number,
    pricing: PricingData[]
  ) => Promise<void>;
  changeRequest?: IChangeRequest | undefined;
  currentChanges?: ICurrentPricingChangesGroup;
}

export default function PricingModal({
  togglePricingModal,
  album,
  readonly,
  updateProductPricing,
  changeRequest,
  currentChanges,
}: PricingModalProps) {
  const { t } = useTranslation();
  const { setNestedModalOpen } = useNestedModalContext();

  const primarySalesTeamIdRef = useRef<string | number | null>(null);

  const handleUpdateProductPricing = async (
    albumId: number,
    pricing: PricingData[]
  ) => {
    const currentPrimaryId = primarySalesTeamIdRef.current;

    const fixedPricing = pricing.map((p) => ({
      ...p,
      isPrimary: Number(p.salesTeamId) === Number(currentPrimaryId),
    }));

    const primary = fixedPricing.find((p) => p.isPrimary);
    const primarySeq = primary?.sequence;

    const modifiedPricing = fixedPricing.map((p) => ({
      ...p,
      isCustomPrice:
        p.isPrimary || primarySeq == null
          ? false
          : p.sequence == null || Number(p.sequence) !== Number(primarySeq),
    }));

    return updateProductPricing(albumId, modifiedPricing);
  };

  const {
    allowedSalesTeams,
    sortedAllowedSalesTeams,
    inactiveSalesTeams,
    primarySalesTeam,
    pricingSelections,
    handlePriceChange,
    primarySalesTeamId,
    onPrimarySalesTeamChange,
    onPrimaryPriceChange,
    handleSubmit,
    onCancel,
    closeWithoutSaving,
    isSubmitting,
    saveErrorMessage,
    setSaveErrorMessage,
    showCancelChangesModal,
    setShowCancelChangesModal,
    handleSelectKeyDown,
  } = usePricingModal(
    album,
    togglePricingModal,
    handleUpdateProductPricing,
    changeRequest
  );
  const allPricingSalesTeams = [...allowedSalesTeams, ...inactiveSalesTeams];

  const {
    hasCurrentChanges,
    getPricingChangeBySalesTeamId,
    getOriginalPriceDisplayValue,
    originalPrimarySalesTeamChange,
    originalPrimaryPriceChange,
    originalPrimarySalesTeamName,
    originalPrimarySalesTeam,
  } = usePricingCurrentChanges({
    currentChanges,
    allSalesTeams: allPricingSalesTeams,
    currentPrimarySalesTeamId: primarySalesTeamId,
  });

  const [showOverrideWarning, setShowOverrideWarning] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    primarySalesTeamIdRef.current = primarySalesTeamId;
  }, [primarySalesTeamId]);

  useEffect(() => {
    setNestedModalOpen(true);
    return () => setNestedModalOpen(false);
  }, [setNestedModalOpen]);

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        !showCancelChangesModal &&
        !showOverrideWarning
      ) {
        onCancel();
      }
    };

    document.addEventListener('keydown', keyDownHandler);
    return () => document.removeEventListener('keydown', keyDownHandler);
  }, [onCancel, showCancelChangesModal, showOverrideWarning]);

  const checkAndCascade = (action: () => void) => {
    let hasCustomPrices = false;
    const primarySelection =
      primarySalesTeamId != null && primarySalesTeam
        ? pricingSelections.get(Number(primarySalesTeamId))
        : null;
    const currentPrimarySeq = primarySelection?.sequence;

    if (currentPrimarySeq == null) {
      action();
      return;
    }

    pricingSelections.forEach((selection, id) => {
      if (Number(id) !== Number(primarySalesTeamId)) {
        const isCustom =
          selection.price != null &&
          (selection.sequence == null ||
            Number(selection.sequence) !== Number(currentPrimarySeq));
        if (isCustom) {
          hasCustomPrices = true;
        }
      }
    });

    if (hasCustomPrices) {
      pendingActionRef.current = action;
      setShowOverrideWarning(true);
    } else {
      action();
    }
  };

  const confirmPriceChange = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;

    try {
      action?.();
    } catch (error) {
      console.error('Failed to apply pricing override:', error);
    } finally {
      setShowOverrideWarning(false);
    }
  };

  const renderRow = (salesTeam: ISalesTeam, isInactive: boolean = false) => {
    const selection = pricingSelections.get(salesTeam.id);
    const currentPrice = selection?.price ?? null;
    const currentSequence = selection?.sequence ?? null;

    const primarySelection =
      primarySalesTeamId != null && primarySalesTeam
        ? pricingSelections.get(Number(primarySalesTeamId))
        : null;
    const primarySequence = primarySelection?.sequence ?? null;
    const isCustomPrice =
      Number(salesTeam.id) === Number(primarySalesTeamId)
        ? false
        : currentPrice != null &&
          primarySequence != null &&
          (currentSequence == null ||
            Number(currentSequence) !== Number(primarySequence));

    const pricingChange = hasCurrentChanges
      ? getPricingChangeBySalesTeamId(salesTeam.id)
      : undefined;

    const { priceOptions, selectValue } = buildPriceDropdownData(
      salesTeam.prices,
      currentPrice,
      currentSequence,
      'sequence'
    );

    return (
      <div className={styles['price-table-row']} key={salesTeam.id}>
        <div className={styles['price-table-cell']}>
          {t(salesTeam.salesTeamKey)}
        </div>
        <div className={styles['price-table-cell']}>
          {salesTeam.currencyName}
        </div>
        <div className={styles['price-table-cell']}>
          <div className={styles['price-input-wrapper']}>
            <div className={styles['price-input-control']}>
              <ChangeRequestIndicator
                show={pricingChange?.price.hasChanges}
                displayValue={getOriginalPriceDisplayValue(
                  pricingChange?.price.value,
                  salesTeam.currencyName
                )}
                customClass={styles['pricing-price-cr-indicator']}
              />
              {!isInactive ? (
                <select
                  className="form-select"
                  value={selectValue}
                  disabled={readonly}
                  onChange={(e) => {
                    handlePriceChange(salesTeam.id, e.target.value);
                  }}
                  onKeyDown={(e) =>
                    handleSelectKeyDown(e, (val) =>
                      handlePriceChange(salesTeam.id, val)
                    )
                  }
                >
                  <option value="">{t('common.generic.select')}</option>
                  {priceOptions.map((p: IPrice) => (
                    <option key={p.sequence} value={p.sequence}>
                      {getCurrencySymbol(salesTeam.currencyName)}{' '}
                      {p.price.toFixed(2)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="form-control-plaintext">
                  {currentPrice !== null && currentPrice !== undefined && (
                    <span>
                      {getCurrencySymbol(salesTeam.currencyName)}{' '}
                      {currentPrice.toFixed(2)}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className={styles['warning-icon-wrapper']}>
              {isCustomPrice && (
                <i
                  className={`icon fas fa-circle-exclamation fa-lg ${styles['warning-icon']}`}
                  title={t('product.pricing.customPriceOverride')}
                ></i>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  let primaryOptions: IPrice[] = [];
  let primarySelectValue = '';

  if (primarySalesTeam) {
    const selection = pricingSelections.get(Number(primarySalesTeam.id));
    const currentPrice = selection?.price ?? null;
    const currentSequence = selection?.sequence ?? null;
    const dropdownData = buildPriceDropdownData(
      primarySalesTeam.prices,
      currentPrice,
      currentSequence,
      'price'
    );
    primaryOptions = dropdownData.priceOptions;
    primarySelectValue = dropdownData.selectValue;
  }

  return (
    <>
      <CancelChangesModal
        onSubmit={handleSubmit}
        onClose={closeWithoutSaving}
        onCancel={() => setShowCancelChangesModal(false)}
        show={showCancelChangesModal}
        titleText={t('product.pricing.editPricing')}
        bodyText={t('product.restrictions.cancelChangesBody')}
      />
      <div
        onPointerDownCapture={(event) => {
          const target = event.target as HTMLElement;
          const confirmButton = target.closest<HTMLButtonElement>(
            'button[type="submit"]'
          );
          if (
            confirmButton &&
            event.button === 0 &&
            event.isPrimary &&
            pendingActionRef.current
          ) {
            confirmButton.click();
          }
        }}
      >
        <ConfirmationModal
          showModal={showOverrideWarning}
          setShowModal={(show: boolean) => {
            setShowOverrideWarning(show);
            if (!show) {
              pendingActionRef.current = null;
            }
          }}
          header={t('product.pricing.overrideWarningHeader')}
          message={t('product.pricing.overrideWarningMessage')}
          handleConfirm={confirmPriceChange}
        />
      </div>
      <div className={`product-edit-mode-modal ${styles['pricing-modal']}`}>
        <div className="product-edit-mode-modal-content">
          <div className="product-edit-mode-modal-header">
            <button
              className="btn-close"
              type="button"
              aria-label="Close"
              onClick={onCancel}
            >
              <i className="icon fas fa-times fa-lg" aria-hidden="true"></i>
            </button>
            <h2>
              {!readonly
                ? t('product.pricing.editPricing')
                : t('product.pricing.viewPricing')}
            </h2>
          </div>

          <div className="product-edit-mode-modal-body">
            <div className="scroll-body">
              <DismissibleErrorAlert
                error={saveErrorMessage}
                onClose={() => setSaveErrorMessage(null)}
              />
              <div className={styles['price-table']}>
                <div className={styles['price-table-header']}>
                  <div className={styles['price-table-cell']}></div>
                  <div className={styles['price-table-cell']}>
                    {t('product.pricing.currency')}
                  </div>
                  <div className={styles['price-table-cell']}>
                    {t('product.pricing.ppd')}
                  </div>
                </div>
                <div className={styles['price-table-body']}>
                  <div className={styles['pricing-section']}>
                    <div className={styles['pricing-section-header']}>
                      {t('product.pricing.salesTerritoritoryTeam')}
                    </div>
                    <div className={styles['price-table-row']}>
                      <div className={styles['price-table-cell']}>
                        <ChangeRequestIndicator
                          show={
                            originalPrimarySalesTeamChange?.isPrimary.hasChanges
                          }
                          displayValue={originalPrimarySalesTeamName}
                          customClass={styles['pricing-territory-cr-indicator']}
                        />
                        <select
                          className="form-select"
                          value={primarySalesTeamId || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            checkAndCascade(() =>
                              onPrimarySalesTeamChange(val)
                            );
                          }}
                          onKeyDown={(e) =>
                            handleSelectKeyDown(e, (val) =>
                              checkAndCascade(() =>
                                onPrimarySalesTeamChange(val)
                              )
                            )
                          }
                          disabled={readonly}
                        >
                          <option value="">{t('common.generic.select')}</option>
                          {sortedAllowedSalesTeams.map((st) => (
                            <option key={st.id} value={st.id}>
                              {t(st.salesTeamKey)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles['price-table-cell']}>
                        {primarySalesTeam?.currencyName}
                      </div>
                      <div className={styles['price-table-cell']}>
                        <ChangeRequestIndicator
                          show={
                            primarySalesTeam &&
                            (originalPrimarySalesTeamChange?.isPrimary
                              .hasChanges ||
                              originalPrimaryPriceChange?.price.hasChanges)
                          }
                          displayValue={getOriginalPriceDisplayValue(
                            originalPrimaryPriceChange?.price.value,
                            originalPrimarySalesTeam?.currencyName ??
                              primarySalesTeam?.currencyName
                          )}
                          customClass={styles['pricing-price-cr-indicator']}
                        />
                        {primarySalesTeam && (
                          <select
                            className={`form-select ${styles['primary-price-select']}`}
                            value={primarySelectValue}
                            disabled={readonly}
                            onChange={(e) => {
                              const val = e.target.value;
                              checkAndCascade(() => onPrimaryPriceChange(val));
                            }}
                            onKeyDown={(e) =>
                              handleSelectKeyDown(e, (val) =>
                                checkAndCascade(() => onPrimaryPriceChange(val))
                              )
                            }
                          >
                            <option value="">
                              {t('common.generic.select')}
                            </option>
                            {primaryOptions.map((p: IPrice) => (
                              <option key={p.sequence} value={p.sequence}>
                                {getCurrencySymbol(
                                  primarySalesTeam.currencyName
                                )}{' '}
                                {p.price.toFixed(2)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles['pricing-section']}>
                    <div className={styles['pricing-section-header']}>
                      {t('product.pricing.salesTerritories')}
                    </div>
                    {sortedAllowedSalesTeams
                      .filter(
                        (t) => Number(t.id) !== Number(primarySalesTeamId)
                      )
                      .map((t) => renderRow(t))}
                  </div>
                  {inactiveSalesTeams.length > 0 && (
                    <div className={styles['pricing-section']}>
                      <div className={styles['pricing-section-header']}>
                        {t('product.pricing.inactiveSalesTerritories')}
                      </div>
                      {inactiveSalesTeams.map((t: any) => renderRow(t, true))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            {!readonly && (
              <>
                <button
                  type="button"
                  className="btn btn-primary alt"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {t('common.button.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary buttonSave"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {t('common.button.save')}
                </button>
              </>
            )}
            {readonly && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                {t('common.button.close')}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
